/**
 * Normalize phone/fax display and storage.
 *
 * Country code 1 (NANP): (XXX) XXX-XXXX
 * Other countries: +<country code> <national digits>
 */

/** Common calling codes, longest first (for +CC######## without a space). */
const CALLING_CODES = [
  '1868', '1869', '1876', '1242', '1246', '1264', '1268', '1284', '1340', '1345',
  '1441', '1473', '1649', '1664', '1670', '1671', '1684', '1721', '1758', '1767',
  '1784', '1787', '1809', '1829', '1849', '1939',
  '998', '996', '995', '994', '993', '992', '977', '976', '975', '974', '973', '972',
  '971', '970', '968', '967', '966', '965', '964', '963', '962', '961', '960',
  '886', '880', '856', '855', '853', '852', '850', '692', '690', '689', '688',
  '687', '686', '685', '683', '682', '681', '680', '679', '678', '677', '676',
  '675', '674', '673', '672', '670', '599', '598', '597', '595', '593', '592',
  '591', '590', '509', '508', '507', '506', '505', '504', '503', '502', '501',
  '500', '423', '421', '420', '389', '387', '386', '385', '383', '382', '381',
  '380', '378', '377', '376', '375', '374', '373', '372', '371', '370', '359',
  '358', '357', '356', '355', '354', '353', '352', '351', '350', '299', '298',
  '297', '291', '290', '269', '268', '267', '266', '265', '264', '263', '262',
  '261', '260', '258', '257', '256', '255', '254', '253', '252', '251', '250',
  '249', '248', '246', '245', '244', '243', '242', '241', '240', '239', '238',
  '237', '236', '235', '234', '233', '232', '231', '230', '229', '228', '227',
  '226', '225', '224', '223', '222', '221', '220', '218', '216', '213', '212',
  '211', '98', '95', '94', '93', '92', '91', '90', '86', '84', '82', '81', '66',
  '65', '64', '63', '62', '61', '60', '58', '57', '56', '55', '54', '53', '52',
  '51', '49', '48', '47', '46', '45', '44', '43', '41', '40', '39', '36', '34',
  '33', '32', '31', '30', '27', '20', '7', '1',
];

export interface ParsedPhone {
  countryCode: string | null;
  nationalDigits: string;
}

export const stripExt = (value: string): { rest: string; ext: string | null } => {
  const match = String(value).match(/^(.*?)(?:\s*(?:ext\.?|x)\s*(\d+))\s*$/i);
  if (!match) {
    return { rest: String(value).trim(), ext: null };
  }
  return { rest: match[1].trim(), ext: match[2] };
};

export const appendExt = (formatted: string, ext: string | null): string =>
  (ext ? `${formatted} x${ext}` : formatted);

export const parsePhone = (value: string | null | undefined): ParsedPhone | null => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const { rest } = stripExt(raw);
  if (!rest) return null;

  const spaced = rest.match(/^\+(\d{1,4})\s+(.*)$/);
  if (spaced) {
    return {
      countryCode: spaced[1],
      nationalDigits: spaced[2].replace(/\D/g, ''),
    };
  }

  const usFormatted = rest.match(/^\((\d{3})\)\s*(\d{3})-(\d{4})$/);
  if (usFormatted) {
    return {
      countryCode: '1',
      nationalDigits: `${usFormatted[1]}${usFormatted[2]}${usFormatted[3]}`,
    };
  }

  const hasPlus = rest.startsWith('+');
  const digits = rest.replace(/\D/g, '');
  if (!digits) return null;

  if (hasPlus) {
    if (digits.startsWith('1') && digits.length >= 11) {
      return { countryCode: '1', nationalDigits: digits.slice(1) };
    }
    for (const code of CALLING_CODES) {
      if (digits.startsWith(code) && digits.length > code.length) {
        return { countryCode: code, nationalDigits: digits.slice(code.length) };
      }
    }
    return { countryCode: null, nationalDigits: digits };
  }

  if (digits.length === 10) {
    return { countryCode: '1', nationalDigits: digits };
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return { countryCode: '1', nationalDigits: digits.slice(1) };
  }

  return { countryCode: null, nationalDigits: digits };
};

export const formatPhoneNumber = (value: string | null | undefined): string | null | undefined => {
  if (value == null) return value;
  const raw = String(value).trim();
  if (!raw) return raw;

  const { rest, ext } = stripExt(raw);
  const parsed = parsePhone(rest);
  if (!parsed || !parsed.nationalDigits) {
    return value;
  }

  const { countryCode, nationalDigits } = parsed;

  if (countryCode === '1') {
    if (nationalDigits.length === 10) {
      const formatted =
        `(${nationalDigits.slice(0, 3)}) ${nationalDigits.slice(3, 6)}-${nationalDigits.slice(6)}`;
      return appendExt(formatted, ext);
    }
    return appendExt(`+1 ${nationalDigits}`, ext);
  }

  if (countryCode) {
    return appendExt(`+${countryCode} ${nationalDigits}`, ext);
  }

  // Incomplete / unparseable — keep original rather than stripping punctuation
  return value;
};

/** Format national digits for the phone-input local field (given selected country code like "+1"). */
export const formatNationalForCountry = (
  countryCodeWithPlus: string,
  nationalInput: string
): string => {
  const digits = String(nationalInput || '').replace(/\D/g, '');
  const cc = String(countryCodeWithPlus || '').replace(/\D/g, '') || '1';
  if (cc === '1' && digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits;
};

/** Build stored value from country select + national input. */
export const buildPhoneValue = (
  countryCodeWithPlus: string,
  nationalInput: string
): string => {
  const cc = String(countryCodeWithPlus || '').replace(/\D/g, '') || '1';
  const digits = String(nationalInput || '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  if (cc === '1' && digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (cc === '1') {
    return `+1 ${digits}`;
  }
  return `+${cc} ${digits}`;
};

/**
 * NANP 555-NXX-XXXX is reserved for fiction / demo use and fails libphonenumber
 * isValid(), but Omni stores many of these on speaker profiles. Accept them when
 * the number is otherwise a possible NANP length/shape.
 */
export const isFictionalNanpNational = (nationalDigits: string | null | undefined): boolean =>
  /^555\d{7}$/.test(String(nationalDigits || ''));
